import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Monitor,
  FileSpreadsheet,
  Edit2,
  Trash2,
  Plus,
  Server,
  Activity,
  Zap
} from "lucide-react";

function DeviceCard({ dev, API_BASE, onEdit, onDelete }) {
  const navigate = useNavigate();
  const [ratios, setRatios] = useState(null);

  const deviceType = ["PAD-01", "PAD-02", "PAD-03"].includes(dev.type)
    ? "analyzer"
    : dev.type === "PRC-15H+TCR"
      ? "reactive"
      : dev.type === "PMA-03"
        ? "multimeter"
        : dev.type === "Grup Arge Klasik Röle"
          ? "relay"
          : dev.type === "Voltampermetre"
            ? "voltampermetre"
            : "other";


  useEffect(() => {
    async function fetchRatios() {
      try {
        const res = await fetch(`${API_BASE}/measurements/ratios?deviceId=${dev.id}`);
        if (!res.ok) throw new Error("HTTP " + res.status);
        const json = await res.json();
        setRatios(json);
      } catch (err) {
        console.error("❌ ratios fetch error:", err);
      }
    }

    fetchRatios();
    const i = setInterval(fetchRatios, 5000);
    return () => clearInterval(i);
  }, [API_BASE, dev.id]);

  const endVal = ratios?.tanPhiEnd ? ratios.tanPhiEnd.toFixed(3) : "0.000";
  const capVal = ratios?.tanPhiCap ? Math.abs(ratios.tanPhiCap).toFixed(3) : "0.000";

  return (
    <div className="relative bg-white rounded-2xl shadow-md p-6 hover:shadow-lg transition">
      <div className="absolute top-2 right-2">
        <button onClick={() => onEdit(dev)} className="p-1 rounded hover:bg-gray-100">
          <Edit2 className="w-4 h-4 text-gray-600" />
        </button>
        <button onClick={() => onDelete(dev)} className="ml-2 p-1 rounded hover:bg-gray-100">
          <Trash2 className="w-4 h-4 text-red-600" />
        </button>
      </div>

      <h2 className="text-xl font-semibold text-indigo-600">{dev.name}</h2>
      <p className="text-gray-600 text-sm mt-2">Cihaz: {dev.type}</p>

      {/* 🌍 Konum gösterimi */}
      {dev.latitude && dev.longitude && (
        <p className="text-gray-400 text-xs mt-1">
          📍 {dev.latitude.toFixed(5)}, {dev.longitude.toFixed(5)}
        </p>
      )}

      {dev.description && <p className="text-gray-400 text-xs mt-2">{dev.description}</p>}

      {dev.last_comm ? (
        <p className="text-xs text-gray-400 mt-2">
          Son Haberleşme:{" "}
          {new Date(dev.last_comm).toLocaleString("tr-TR", {
            dateStyle: "short",
            timeStyle: "short",
          })}
        </p>
      ) : (
        <p className="text-xs text-red-400 mt-2">Son Haberleşme: Yok</p>
      )}

      <div className="flex justify-between mt-4 text-sm font-semibold">
        <span className="text-green-600">Endüktif: {endVal}</span>
        <span className="text-red-600">Kapasitif: {capVal}</span>
      </div>

      <div className="flex gap-3 mt-4">
        <button
          onClick={() => {
            localStorage.setItem("deviceType", deviceType);
            localStorage.setItem("deviceModel", dev.type);
            navigate(`/admin/devices/${dev.id}/${deviceType}/real-time`);
          }}
          className="flex-1 bg-indigo-500 text-white px-4 py-2 rounded-md text-sm hover:bg-indigo-600"
        >
          Veriler
        </button>

      </div>
    </div>
  );
}

export default function AdminDevices() {
  const { gatewayId } = useParams();
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState("");
  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  async function fetchDevices() {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/gateways/${gatewayId}/devices`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setDevices(Array.isArray(data) ? data : data.devices || []);
    } catch (err) {
      console.error("Error fetching devices:", err);
    }
  }

  useEffect(() => {
    fetchDevices();
  }, [API_BASE, gatewayId]);

  const handleEdit = (dev) => {
    setSelectedDevice(dev);
    setError("");
    setShowModal(true);
  };

  const handleDelete = async (dev) => {
    if (!window.confirm(`${dev.name} cihazını silmek istediğine emin misin?`)) return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/gateways/devices/${dev.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      fetchDevices();
    } catch (err) {
      console.error("❌ Delete device error:", err);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const form = e.target;

    if (!form.name.value.trim()) {
      setError("Cihaz adı boş olamaz.");
      return;
    }
    if (!form.type.value.trim()) {
      setError("Tip boş olamaz.");
      return;
    }
    if (!form.address.value.trim()) {
      setError("Adres boş olamaz.");
      return;
    }

    setError("");

    const payload = {
      name: form.name.value,
      type: form.type.value,
      address: form.address.value,
      description: form.description.value,
      gateway_id: gatewayId,
      latitude: form.latitude.value ? Number(form.latitude.value) : null,  // 🌍
      longitude: form.longitude.value ? Number(form.longitude.value) : null, // 🌍
    };

    try {
      const token = localStorage.getItem("token");
      const url = selectedDevice
        ? `${API_BASE}/gateways/devices/${selectedDevice.id}`
        : `${API_BASE}/gateways/devices`;
      const method = selectedDevice ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setShowModal(false);
      setSelectedDevice(null);
      fetchDevices();
    } catch (err) {
      console.error("❌ Save device error:", err);
      setError("Kaydetme sırasında hata oluştu.");
    }
  };

  const analyzerCount = devices.filter((d) =>
    ["PAD-01", "PAD-02", "PAD-03"].includes(d.type)
  ).length;

  const reactiveCount = devices.filter((d) => d.type === "PRC-15H+TCR").length;

  const multimeterCount = devices.filter((d) => d.type === "PMA-03").length;

  const relayCount = devices.filter((d) => d.type === "Grup Arge Klasik Röle").length;

  return (
    <div className="p-8 min-h-screen bg-gray-50">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Cihazlar</h1>
        <button
          onClick={() => {
            setSelectedDevice(null);
            setError("");
            setShowModal(true);
          }}
          className="flex items-center gap-2 bg-indigo-500 text-white px-4 py-2 rounded-md hover:bg-indigo-600"
        >
          <Plus className="w-4 h-4" />
          Cihaz Ekle
        </button>
      </div>

      {/* Özet Kartlar */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {analyzerCount > 0 && (
          <div className="flex bg-white rounded-xl shadow-md overflow-hidden w-full sm:w-64">
            <div className="bg-orange-500 flex items-center justify-center w-20">
              <Monitor className="w-8 h-8 text-white" />
            </div>
            <div className="flex-1 text-center py-4">
              <p className="text-2xl font-bold text-gray-800">{analyzerCount}</p>
              <p className="text-gray-500 text-sm">Analizör</p>
            </div>
          </div>
        )}
        {reactiveCount > 0 && (
          <div className="flex bg-white rounded-xl shadow-md overflow-hidden w-full sm:w-64">
            <div className="bg-purple-500 flex items-center justify-center w-20">
              <Server className="w-8 h-8 text-white" />
            </div>
            <div className="flex-1 text-center py-4">
              <p className="text-2xl font-bold text-gray-800">{reactiveCount}</p>
              <p className="text-gray-500 text-sm">Reaktif</p>
            </div>
          </div>
        )}
        {/* ✅ Multimetre kartı */}
        {multimeterCount > 0 && (
          <div className="flex bg-white rounded-xl shadow-md overflow-hidden w-full sm:w-64">
            <div className="bg-green-500 flex items-center justify-center w-20">
              <Activity className="w-8 h-8 text-white" />
            </div>
            <div className="flex-1 text-center py-4">
              <p className="text-2xl font-bold text-gray-800">{multimeterCount}</p>
              <p className="text-gray-500 text-sm">Multimetre</p>
            </div>
          </div>
        )}
        {/* ✅ Röle kartı */}
        {relayCount > 0 && (
          <div className="flex bg-white rounded-xl shadow-md overflow-hidden w-full sm:w-64">
            <div className="bg-blue-500 flex items-center justify-center w-20">
              <Zap className="w-8 h-8 text-white" />
            </div>
            <div className="flex-1 text-center py-4">
              <p className="text-2xl font-bold text-gray-800">{relayCount}</p>
              <p className="text-gray-500 text-sm">Röle</p>
            </div>
          </div>
        )}
      </div>

      {/* Cihaz Kartları */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {devices.map((dev) => (
          <DeviceCard
            key={dev.id}
            dev={dev}
            API_BASE={API_BASE}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        ))}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
            <h2 className="text-lg font-bold mb-4">
              {selectedDevice ? "Cihazı Düzenle" : "Yeni Cihaz"}
            </h2>

            {error && (
              <div className="mb-4 text-sm text-red-600 bg-red-100 p-2 rounded">
                {error}
              </div>
            )}

            <form onSubmit={handleSave} className="space-y-4">
              <input
                name="name"
                defaultValue={selectedDevice?.name || ""}
                placeholder="Cihaz adı *"
                className="w-full border p-2 rounded"
                required
              />
              <select
                name="type"
                defaultValue={selectedDevice?.type || ""}
                className="w-full border p-2 rounded"
                required
              >
                <option value="">-- Cihaz Tipi Seçin --</option>
                <option value="PAD-01">PAD-01</option>
                <option value="PAD-02">PAD-02</option>
                <option value="PAD-03">PAD-03</option>
                <option value="PRC-15H+TCR">PRC-15H+TCR</option>
                <option value="PMA-03">PMA-03</option>
                <option value="Grup Arge Klasik Röle">Grup Arge Klasik Röle</option>
                <option value="Voltampermetre">Voltampermetre</option>
              </select>
              <input
                name="address"
                defaultValue={selectedDevice?.address || ""}
                placeholder="Adres *"
                className="w-full border p-2 rounded"
                required
              />
              <textarea
                name="description"
                defaultValue={selectedDevice?.description || ""}
                placeholder="Açıklama"
                className="w-full border p-2 rounded"
              />

              {/* 🌍 Konum Bilgileri */}
              <div className="grid grid-cols-2 gap-4">
                <input
                  name="latitude"
                  type="number"
                  step="0.000001"
                  defaultValue={selectedDevice?.latitude || ""}
                  placeholder="Latitude (örn: 38.433)"
                  className="w-full border p-2 rounded"
                />
                <input
                  name="longitude"
                  type="number"
                  step="0.000001"
                  defaultValue={selectedDevice?.longitude || ""}
                  placeholder="Longitude (örn: 27.145)"
                  className="w-full border p-2 rounded"
                />
              </div>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-500 text-white rounded hover:bg-indigo-600"
                >
                  Kaydet
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
